import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { 
  User, 
  Prescription, 
  Patient, 
  Medication, 
  ProcessingMetrics,
  PrescriptionStatus,
  QueuePriority,
  UserRole,
  TimeTracking
} from '../types/pharmacy';
import { pharmacyApi } from '../services/pharmacyApi';

// State interface
interface PharmacyState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  
  // Current prescription being processed
  currentPrescription: Prescription | null;
  
  // Processing queue
  processingQueue: any[];
  metrics: ProcessingMetrics | null;
  
  // Templates
  assessmentTemplates: any[];
  
  // Time tracking
  startTime: number | null;
  processingTimes: number[];
}

// Action types
type PharmacyAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: { user: User } }
  | { type: 'AUTH_FAILURE'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'SET_PRESCRIPTION'; payload: Prescription }
  | { type: 'CLEAR_ERROR' }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_METRICS'; payload: ProcessingMetrics }
  | { type: 'UPDATE_PROCESSING_TIME'; payload: number };

// Initial state
const initialState: PharmacyState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  currentPrescription: null,
  processingQueue: [],
  metrics: null,
  assessmentTemplates: [],
  startTime: null,
  processingTimes: [],
};

// Reducer
const pharmacyReducer = (state: PharmacyState, action: PharmacyAction): PharmacyState => {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    
    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
    
    case 'AUTH_FAILURE':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };
    
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        currentPrescription: null,
        error: null,
      };
    
    case 'SET_PRESCRIPTION':
      return {
        ...state,
        currentPrescription: action.payload,
        startTime: Date.now(),
      };
    
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };
    
    case 'SET_METRICS':
      return {
        ...state,
        metrics: action.payload,
      };
    
    case 'UPDATE_PROCESSING_TIME':
      const newTime = action.payload;
      const updatedTimes = [...state.processingTimes, newTime];
      return {
        ...state,
        processingTimes: updatedTimes,
      };
    
    default:
      return state;
  }
};

// Context
interface PharmacyContextType {
  state: PharmacyState;
  login: (credentials: { email: string; password: string }) => Promise<void>;
  logout: () => void;
  clearError: () => void;
  setCurrentPrescription: (prescription: Prescription) => void;
  loadMetrics: () => Promise<void>;
  startProcessingTimer: () => void;
  stopProcessingTimer: () => void;
  logTimeTracking: (data: Partial<TimeTracking>) => Promise<void>;
  hasRole: (role: UserRole) => boolean;
}

const PharmacyContext = createContext<PharmacyContextType | undefined>(undefined);

// Provider component
interface PharmacyProviderProps {
  children: ReactNode;
}

export const PharmacyProvider: React.FC<PharmacyProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(pharmacyReducer, initialState);

  // Login function
  const login = async (credentials: { email: string; password: string }) => {
    try {
      dispatch({ type: 'AUTH_START' });
      
      // For now, simulate login (replace with actual API call)
      const mockUser: User = {
        id: 1,
        email: credentials.email,
        username: credentials.email.split('@')[0],
        full_name: 'Alberta Pharmacist',
        role: UserRole.PHARMACIST,
        is_active: true,
        created_at: new Date().toISOString(),
      };
      
      localStorage.setItem('access_token', 'mock_token');
      dispatch({ type: 'AUTH_SUCCESS', payload: { user: mockUser } });
    } catch (error: any) {
      dispatch({ type: 'AUTH_FAILURE', payload: error.message || 'Login failed' });
      throw error;
    }
  };

  // Logout function
  const logout = () => {
    localStorage.removeItem('access_token');
    dispatch({ type: 'LOGOUT' });
  };

  // Clear error
  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  // Set current prescription
  const setCurrentPrescription = (prescription: Prescription) => {
    dispatch({ type: 'SET_PRESCRIPTION', payload: prescription });
  };

  // Load metrics
  const loadMetrics = async () => {
    try {
      // Use mock data instead of API call for demo
      const mockMetrics = {
        prescriptions_processed: 24,
        average_processing_time: 300, // 5 minutes
        error_rate: 2.5,
        throughput_per_hour: 12,
        time_saved: 6.5, // hours saved today
        date: new Date().toISOString(),
      };
      
      dispatch({ type: 'SET_METRICS', payload: mockMetrics });
    } catch (error: any) {
      dispatch({ type: 'AUTH_FAILURE', payload: error.message || 'Failed to load metrics' });
    }
  };

  // Timer functions
  const startProcessingTimer = () => {
    dispatch({ type: 'SET_PRESCRIPTION', payload: state.currentPrescription! });
  };

  const stopProcessingTimer = () => {
    if (state.startTime) {
      const processingTime = Math.floor((Date.now() - state.startTime) / 1000);
      dispatch({ type: 'UPDATE_PROCESSING_TIME', payload: processingTime });
    }
  };

  // Role checking
  const hasRole = (role: UserRole): boolean => {
    return state.user?.role === role;
  };

  // Load initial data
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token) {
      // Load user from token (simplified for now)
      const mockUser: User = {
        id: 1,
        email: 'pharmacist@alberta.pharmacy',
        username: 'pharmacist',
        full_name: 'Alberta Pharmacist',
        role: UserRole.PHARMACIST,
        is_active: true,
        created_at: new Date().toISOString(),
      };
      dispatch({ type: 'AUTH_SUCCESS', payload: { user: mockUser } });
      
      // Load metrics
      loadMetrics();
    }
  }, []);

  // Log time tracking
  const logTimeTracking = async (data: Partial<TimeTracking>) => {
    try {
      await pharmacyApi.logTimeTracking(data);
    } catch (error: any) {
      dispatch({ type: 'AUTH_FAILURE', payload: error.message || 'Failed to log time tracking' });
    }
  };

  const value: PharmacyContextType = {
    state,
    login,
    logout,
    clearError,
    setCurrentPrescription,
    loadMetrics,
    startProcessingTimer,
    stopProcessingTimer,
    logTimeTracking,
    hasRole,
  };

  return (
    <PharmacyContext.Provider value={value}>
      {children}
    </PharmacyContext.Provider>
  );
};

// Hook to use context
export const usePharmacy = () => {
  const context = useContext(PharmacyContext);
  if (context === undefined) {
    throw new Error('usePharmacy must be used within a PharmacyProvider');
  }
  return context;
};

export default PharmacyContext;
